// 地球半径（米）
const EARTH_RADIUS = 6371008.8 // 实际上就是 6371008.8

// 单位转换常量
const FACTORS = {
  centimeters: EARTH_RADIUS * 100,
  centimetres: EARTH_RADIUS * 100,
  degrees: 360 / (2 * Math.PI),
  feet: EARTH_RADIUS * 3.28084,
  inches: EARTH_RADIUS * 39.37,
  kilometers: EARTH_RADIUS / 1000,
  kilometres: EARTH_RADIUS / 1000,
  meters: EARTH_RADIUS,
  metres: EARTH_RADIUS,
  miles: EARTH_RADIUS / 1609.344,
  millimeters: EARTH_RADIUS * 1000,
  millimetres: EARTH_RADIUS * 1000,
  nauticalmiles: EARTH_RADIUS / 1852,
  radians: 1,
  yards: EARTH_RADIUS * 1.0936
}

// 通用工具函数
function isNumber (value) {
  return !isNaN(value) && value !== null && !Array.isArray(value)
}

function isObject (value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

// 创建 GeoJSON Feature
function feature (geometry, properties = {}, options = {}) {
  const feat = {
    type: 'Feature'
  }

  if (options.id === 0 || options.id) {
    feat.id = options.id
  }

  if (options.bbox) {
    feat.bbox = options.bbox
  }

  feat.properties = properties || {}
  feat.geometry = geometry

  return feat
}

// 创建 Point Feature
function point (coordinates, properties, options = {}) {
  if (!coordinates) throw new Error('coordinates is required')
  if (!Array.isArray(coordinates)) throw new Error('coordinates must be an Array')
  if (coordinates.length < 2) throw new Error('coordinates must be at least 2 numbers long')
  if (!isNumber(coordinates[0]) || !isNumber(coordinates[1])) {
    throw new Error('coordinates must contain numbers')
  }

  return feature({
    type: 'Point',
    coordinates
  }, properties, options)
}

// 创建 Polygon Feature
function polygon (coordinates, properties, options = {}) {
  for (const ring of coordinates) {
    if (ring.length < 4) {
      throw new Error('Each LinearRing of a Polygon must have 4 or more Positions.')
    }

    // 检查首尾点是否相同
    if (ring[ring.length - 1].length !== ring[0].length) {
      throw new Error('First and last Position are not equivalent.')
    }

    for (let i = 0; i < ring[ring.length - 1].length; i++) {
      if (ring[ring.length - 1][i] !== ring[0][i]) {
        throw new Error('First and last Position are not equivalent.')
      }
    }
  }

  return feature({
    type: 'Polygon',
    coordinates
  }, properties, options)
}

// 创建 LineString Feature
function lineString (coordinates, properties, options = {}) {
  if (coordinates.length < 2) {
    throw new Error('coordinates must be an array of two or more positions')
  }

  return feature({
    type: 'LineString',
    coordinates
  }, properties, options)
}

// 创建 FeatureCollection
function featureCollection (features, options = {}) {
  const collection = {
    type: 'FeatureCollection'
  }

  if (options.id) {
    collection.id = options.id
  }

  if (options.bbox) {
    collection.bbox = options.bbox
  }

  collection.features = features
  return collection
}

// 创建 MultiLineString Feature
function multiLineString (coordinates, properties, options = {}) {
  return feature({
    type: 'MultiLineString',
    coordinates
  }, properties, options)
}

// 弧度转换为距离
function radiansToLength (radians, unit = 'kilometers') {
  const factor = FACTORS[unit]
  if (!factor) throw new Error(`${unit} units is invalid`)
  return radians * factor
}

// 距离转换为弧度
function lengthToRadians (distance, unit = 'kilometers') {
  const factor = FACTORS[unit]
  if (!factor) throw new Error(`${unit} units is invalid`)
  return distance / factor
}

// 弧度转换为角度
function radiansToDegrees (radians) {
  return (radians % (2 * Math.PI)) * 180 / Math.PI
}

// 角度转换为弧度
function degreesToRadians (degrees) {
  return (degrees % 360) * Math.PI / 180
}

// 转换距离单位
function convertLength (length, originalUnit = 'kilometers', finalUnit = 'kilometers') {
  if (!(length >= 0)) throw new Error('length must be a positive number')
  return radiansToLength(lengthToRadians(length, originalUnit), finalUnit)
}

// 标准化坐标为数组
function coord (geojson) {
  if (!geojson) throw new Error('coord is required')

  if (!Array.isArray(geojson)) {
    if (geojson.type === 'Feature' &&
        geojson.geometry !== null &&
        geojson.geometry.type === 'Point') {
      return [...geojson.geometry.coordinates]
    }

    if (geojson.type === 'Point') {
      return [...geojson.coordinates]
    }
  }

  if (Array.isArray(geojson) &&
      geojson.length >= 2 &&
      !Array.isArray(geojson[0]) &&
      !Array.isArray(geojson[1])) {
    return [...geojson]
  }

  throw new Error('coord must be GeoJSON Point or an Array of numbers')
}

// 获取坐标数组
function getCoords (geojson) {
  if (Array.isArray(geojson)) return geojson

  if (geojson.type === 'Feature') {
    if (geojson.geometry !== null) return geojson.geometry.coordinates
  } else if (geojson.coordinates) {
    return geojson.coordinates
  }

  throw new Error('coords must be GeoJSON Feature, Geometry Object or an Array')
}

// 获取几何体
function getGeom (geojson) {
  return geojson.type === 'Feature' ? geojson.geometry : geojson
}

// 获取几何体类型
function getType (geojson) {
  if (geojson.type === 'FeatureCollection') return 'FeatureCollection'
  if (geojson.type === 'GeometryCollection') return 'GeometryCollection'
  if (geojson.type === 'Feature' && geojson.geometry !== null) {
    return geojson.geometry.type
  }
  return geojson.type
}

// 根据起始点、距离和方位角计算目标点
function destination (origin, distance, bearing, options = {}) {
  const originCoord = coord(origin)
  const lonRad = degreesToRadians(originCoord[0])
  const latRad = degreesToRadians(originCoord[1])

  const bearingRad = degreesToRadians(bearing)
  const angularDistance = lengthToRadians(distance, options.units)

  const destLatRad = Math.asin(
    Math.sin(latRad) * Math.cos(angularDistance) +
    Math.cos(latRad) * Math.sin(angularDistance) * Math.cos(bearingRad)
  )

  const destLonRad = lonRad + Math.atan2(
    Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(latRad),
    Math.cos(angularDistance) - Math.sin(latRad) * Math.sin(destLatRad)
  )

  const destLon = radiansToDegrees(destLonRad)
  const destLat = radiansToDegrees(destLatRad)

  return point([destLon, destLat], options.properties)
}

// 导出所有函数
export {
  EARTH_RADIUS,
  FACTORS,
  isNumber,
  isObject,
  feature,
  point,
  polygon,
  lineString,
  featureCollection,
  multiLineString,
  radiansToLength,
  lengthToRadians,
  radiansToDegrees,
  degreesToRadians,
  convertLength,
  coord,
  getCoords,
  getGeom,
  getType,
  destination
}
